import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";

const inputClass =
  "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

const IMAGE_BASE = process.env.REACT_APP_API_URL || "http://localhost:3010";

/**
 * Pictures already saved (removable) plus new files to upload, with previews.
 *
 * The parent owns what gets sent: `existing` is the list of kept paths, and
 * `onFilesChange` reports the chosen files. Previews are made and released
 * here, so the parent never has to clean up object URLs.
 */
const ImagePicker = ({ existing, onExistingChange, onFilesChange, editing }) => {
  const { t } = useI18n();
  const [previews, setPreviews] = useState([]);

  // Each set of previews is released when it is replaced, and on unmount
  useEffect(
    () => () => previews.forEach((url) => URL.revokeObjectURL(url)),
    [previews],
  );

  const select = (e) => {
    const files = Array.from(e.target.files);

    setPreviews(files.map((file) => URL.createObjectURL(file)));
    onFilesChange(files);
  };

  const clearNew = () => {
    setPreviews([]);
    onFilesChange([]);
  };

  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t("products.form.images")}
      </h3>

      {existing.length > 0 && (
        <div className="mb-4">
          <label className={labelClass}>{t("products.form.currentImages")}</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {existing.map((img, index) => (
              <div key={index} className="relative group">
                <img
                  src={`${IMAGE_BASE}${img}`}
                  alt=""
                  className="w-full h-32 object-cover rounded-lg border"
                />
                <button
                  type="button"
                  onClick={() =>
                    onExistingChange(existing.filter((_, i) => i !== index))
                  }
                  aria-label={t("products.form.removeImage")}
                  className="absolute top-2 end-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className={labelClass}>
          {editing
            ? t("products.form.addImages")
            : t("products.form.uploadImages")}
        </label>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={select}
          className={inputClass}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t("products.form.imagesHint")}
        </p>
      </div>

      {previews.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {t("products.form.newPreview")}
            </label>
            <button
              type="button"
              onClick={clearNew}
              className="text-sm text-red-600 hover:text-red-800"
            >
              {t("products.form.clearAll")}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {previews.map((preview, index) => (
              <div key={index} className="relative">
                <img
                  src={preview}
                  alt=""
                  className="w-full h-32 object-cover rounded-lg border"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagePicker;
